import { cn } from '@heidi/utils';

import { focusRing } from '../styles/focus-ring';
import { typographyVariant } from '../typography';

/** Root container — horizontal bar holding list + actions. */
export const artifactTabsClasses = 'flex items-center gap-1';

/**
 * Scrollable list wrapper — positions the fade edges. Vertical overflow stays visible
 * so the active pill's shadow isn't clipped (horizontal clipping is handled by the inner
 * scroller below).
 *
 * `flex: 0 1 auto` (the default — no `flex-1`) + `min-w-0`: the list sizes to its pills so the
 * trailing actions (e.g. "+ Create") sit immediately after the tabs rather than being pushed
 * to the far edge, yet it can still shrink below its content (and the inner scroller takes
 * over) once the pills overflow the available width. The bar's own flex-1 (set by the
 * consumer on the root) absorbs any leftover space after the actions.
 */
export const artifactTabListClasses = 'relative min-w-0';

/**
 * Inner scrollable list (Radix TabsList).
 * `overflow-x-auto` forces `overflow-y` to compute as `auto` (CSS can't pair a
 * scrollable axis with a visible one), which would clip the active pill's shadow.
 * `py-2` gives the shadow vertical room inside the clip box; `-my-2` cancels it so
 * the bar height is unchanged.
 */
export const artifactTabListInnerClasses =
  'flex items-center gap-1 overflow-x-auto scrollbar-none py-2 -my-2';

/**
 * Fade overlay classes (left/right gradient visibility). The component toggles their
 * opacity from the horizontal scroll position. `-inset-y-1` overshoots the clip box so the
 * gradient covers the full pill height; `w-10` sets the fade width.
 */
export const artifactTabFadeLeftClasses =
  '-inset-y-1 -left-1 w-10 transition-opacity duration-200';
export const artifactTabFadeRightClasses =
  '-inset-y-1 -right-1 w-10 transition-opacity duration-200';

/** Pill-shaped tab trigger. */
export const artifactTabItemClasses = cn(
  // Base tab — matches Button size="sm" inline geometry (min-w-8 rounded-md px-2.5), but is
  // 30px tall, not 32px: the header's neighbouring surface/outline Buttons are a 32px box with a
  // visible 1px border, so their *coloured interior* is only 30px. Sizing the tab to that interior
  // (not the outer box) keeps the tab's fill optically level with the buttons. The 1px transparent
  // border is kept (built into the 30px via border-box) so the focus ring + box-model stay
  // consistent. (DS-786)
  // `max-w-48` caps a single pill so one long label can't balloon the tab and crowd out the bar's
  // actions — the label truncates with an ellipsis (see artifactTabItemLabelClasses). The
  // bar/cluster width is the consumer's concern (the header's overflow hook), not this. (DS-778)
  'inline-flex h-[30px] min-w-8 max-w-48 shrink-0 items-center justify-center rounded-md',
  // Icon + label spacing — matches Button size="sm" (gap-x-1)
  'gap-x-1 px-2.5',
  // Colour transition runs ONLY while the pill is pointer-hovered. Keyboard navigation
  // and selection never set `:hover`, so they have no transition at all — the label
  // colour snaps. (The active-pill background is also instant, never in any transition.)
  // This is why there's no `:has(:focus-visible)` suppression: there's nothing to
  // suppress off the hover path.
  'hover:transition-[border-color,color] hover:duration-200 hover:ease-out',
  // Icon sizing — matches Button size="sm" (size-3.5). The :not guard lets a caller
  // override the size per-icon.
  '[&_svg:not([class*="size-"])]:size-3.5 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  // Inactive state — transparent fill, no visible border (border kept transparent to preserve box-model)
  'border border-transparent text-text-secondary',
  // Hover — primary text + a surface overlay lightening toward the selected pill,
  // matching the header's `surface` Button variant (`hover:bg-surface/50`).
  // newBrand: 50% white over the sand page. newBrandDark: `bg-surface/50` is the
  // near-black surface and reads as no lift, so a faint white overlay is kept for
  // the dark theme (higher specificity → wins over the light-theme rule).
  'hover:bg-surface/50 hover:text-text-primary newBrandDark:hover:bg-white/10',
  // Dismissible tabs: the ✕ overlays the trigger, so hovering it drops the trigger's own
  // `:hover`. Re-apply the inactive lift via the wrapper's hover so the pill stays lifted over
  // the ✕. Scoped to inactive (never dilutes the active pill); same values as `:hover` above.
  'data-[state=inactive]:[[data-slot=artifact-tab-item]:hover_&]:bg-surface/50 data-[state=inactive]:[[data-slot=artifact-tab-item]:hover_&]:text-text-primary newBrandDark:data-[state=inactive]:[[data-slot=artifact-tab-item]:hover_&]:bg-white/10',
  'data-[state=inactive]:[[data-slot=artifact-tab-item]:hover_&]:transition-[border-color,color] data-[state=inactive]:[[data-slot=artifact-tab-item]:hover_&]:duration-200 data-[state=inactive]:[[data-slot=artifact-tab-item]:hover_&]:ease-out',
  // Open-menu lit state: while a descendant trigger (the kebab) has an open surface
  // (`data-state=open`), keep the inactive pill lit at the same values as `:hover` so the
  // trigger ↔ open-menu pairing stays visible. Same signal the kebab reveal and icon swap
  // above key off, so all three affordances stay in lockstep.
  'data-[state=inactive]:[[data-slot=artifact-tab-item]:has([data-state=open])_&]:bg-surface/50 data-[state=inactive]:[[data-slot=artifact-tab-item]:has([data-state=open])_&]:text-text-primary newBrandDark:data-[state=inactive]:[[data-slot=artifact-tab-item]:has([data-state=open])_&]:bg-white/10',
  // Focus ring — the design-system interactive norm: an outset soft `border-focus/50`
  // ring sitting on the pill's edge (matches checkbox/button/item). The `py-2 -my-2` on
  // the scroll list gives the ring vertical room so it isn't clipped; only the outer edge
  // of the first/last tab can crop at a scroll extreme, which is acceptable.
  focusRing,
  // Disabled
  'disabled:pointer-events-none disabled:opacity-50',
  // Active state — a raised white pill applied directly to the active trigger (no
  // transition, so it appears immediately on selection). Primary text on the fill.
  // newBrand: `bg-surface` (white) over the sand page — clearly visible.
  // newBrandDark: `surface` is neutral/900 (≈ the near-black page), so the pill would
  // vanish; a 20% white overlay lifts it to a clearly-visible raised pill, sitting above
  // the white/10 hover so the inactive < hover < active hierarchy holds.
  'data-[state=active]:bg-surface data-[state=active]:text-text-primary data-[state=active]:shadow-sm newBrandDark:data-[state=active]:bg-white/20'
);

/** Content wrapper inside a tab trigger (icon + label). `min-w-0` lets it shrink below its
 * content so the label can truncate at the pill's max-width (the icon stays `shrink-0`). */
export const artifactTabItemContentClasses =
  'inline-flex min-w-0 items-center gap-[inherit]';

/**
 * Positioned wrapper for a *dismissible* tab. `relative` anchors the absolutely-positioned
 * close button, which lives *outside* the Radix trigger `<button>` (no nested buttons). It
 * carries `data-slot="artifact-tab-item"` — the ancestor the close-button reveal, label
 * fade-mask, and inactive-lift selectors hang off (`:hover` / `:has(:focus-visible)`).
 */
export const artifactTabItemWrapperClasses = 'relative inline-flex shrink-0';

/**
 * Label fade for a *dismissible* pill — no layout shift. While the ✕ is revealed (tab hovered
 * or keyboard-focused) the label's trailing ~0.75rem is masked transparent so the overlaid ✕
 * doesn't collide with the text. The mask is a `--af-tab-mask` var (default `none`) flipped on
 * reveal; `#000`/`transparent` are alpha, so it exposes the real pill background in any theme
 * with no colour-matching. LTR fades the end, RTL the start; `-webkit-` covers the macOS
 * WKWebView. Snaps in (mask isn't animatable), in lockstep with the ✕.
 */
export const artifactTabItemContentDismissibleClasses = cn(
  '[-webkit-mask-image:var(--af-tab-mask,none)] [mask-image:var(--af-tab-mask,none)]',
  '[[data-slot=artifact-tab-item]:hover_&]:[--af-tab-mask:linear-gradient(to_right,#000_calc(100%_-_1.25rem),transparent_calc(100%_-_0.75rem))]',
  '[[data-slot=artifact-tab-item]:has(:focus-visible)_&]:[--af-tab-mask:linear-gradient(to_right,#000_calc(100%_-_1.25rem),transparent_calc(100%_-_0.75rem))]',
  'rtl:[[data-slot=artifact-tab-item]:hover_&]:[--af-tab-mask:linear-gradient(to_left,#000_calc(100%_-_1.25rem),transparent_calc(100%_-_0.75rem))]',
  'rtl:[[data-slot=artifact-tab-item]:has(:focus-visible)_&]:[--af-tab-mask:linear-gradient(to_left,#000_calc(100%_-_1.25rem),transparent_calc(100%_-_0.75rem))]'
);

/**
 * Close (✕) button for a dismissible tab. Absolutely positioned over the pill's trailing edge
 * (overlays without shifting layout); revealed when the tab is hovered or keyboard-focused.
 *
 * Show/hide uses `visibility`, NOT `opacity`: an opacity-revealed button tints its own
 * `hover:bg-surface-hover` square by the reveal opacity (and the translucent inactive pill
 * shows through), so it looked washed. Visibility keeps the ✕ fully opaque when shown, so the
 * hover square renders solid. Snaps in/out (no fade). `-translate-y-1/2` is static centring.
 */
export const artifactTabDismissButtonClasses = cn(
  'absolute end-1 top-1/2 z-20 -translate-y-1/2',
  'inline-flex size-5 items-center justify-center rounded-sm',
  '[&_svg]:pointer-events-none [&_svg]:size-3.5 [&_svg]:shrink-0',
  'text-text-secondary hover:bg-surface-hover hover:text-text-primary',
  focusRing,
  // Hidden unless the tab is hovered or keyboard-focused. `:has(:focus-visible)` (not
  // `:focus-within`) so a mouse click doesn't keep the ✕ shown after the pointer leaves.
  'pointer-events-none invisible',
  '[[data-slot=artifact-tab-item]:hover_&]:pointer-events-auto [[data-slot=artifact-tab-item]:hover_&]:visible',
  '[[data-slot=artifact-tab-item]:has(:focus-visible)_&]:pointer-events-auto [[data-slot=artifact-tab-item]:has(:focus-visible)_&]:visible'
);

/**
 * Kebab (⋯) menu button for a tab. It sits at the pill's **leading edge in place of the tab
 * icon** (positioned `start-2.5` by the component) and is revealed exactly like the ✕: hidden by
 * default (the tab shows its icon), shown when the tab is hovered or keyboard-focused, and kept
 * visible while its menu is open (`data-[state=open]`, set by the menu primitive's trigger via
 * `asChild`). On reveal the component hides the leading icon, so it reads as an icon→kebab swap.
 *
 * Box + look mirror the dismiss (✕) button exactly: a 20px (`size-5`) `rounded-sm` square with a
 * 14px glyph and a `surface-hover` background — only the edge it hugs differs (leading vs
 * trailing). The 14px glyph matches the static-tab icon's size; the in-flow icon slot (sized to
 * the glyph) keeps the label offset identical whether the tab shows an icon or this button.
 */
export const artifactTabMenuButtonClasses = cn(
  'absolute top-1/2 z-20 -translate-y-1/2',
  'inline-flex size-5 items-center justify-center rounded-sm',
  '[&_svg]:pointer-events-none [&_svg]:size-3.5 [&_svg]:shrink-0',
  'text-text-secondary hover:bg-surface-hover hover:text-text-primary',
  focusRing,
  'pointer-events-none invisible',
  '[[data-slot=artifact-tab-item]:hover_&]:pointer-events-auto [[data-slot=artifact-tab-item]:hover_&]:visible',
  '[[data-slot=artifact-tab-item]:has(:focus-visible)_&]:pointer-events-auto [[data-slot=artifact-tab-item]:has(:focus-visible)_&]:visible',
  'data-[state=open]:pointer-events-auto data-[state=open]:visible'
);

/**
 * Leading icon slot for a tab that has a menu. Sized to the glyph (same footprint as a bare
 * static-tab icon, so the icon position and label offset are identical), and hidden on the same
 * conditions the kebab is revealed — tab hover, keyboard focus, or the menu being open — so the
 * icon and kebab swap in lockstep, in place.
 */
export const artifactTabItemMenuIconClasses = cn(
  'inline-flex shrink-0 items-center justify-center',
  '[[data-slot=artifact-tab-item]:hover_&]:invisible',
  '[[data-slot=artifact-tab-item]:has(:focus-visible)_&]:invisible',
  '[[data-slot=artifact-tab-item]:has([data-state=open])_&]:invisible'
);

/**
 * Overlay that covers the pill while an inline rename form is active. Sits above the trigger and
 * its affordances (`z-30`) with a solid `bg-surface` pill so the label behind never shows
 * through, and carries the pill's own rounding/border so it reads as an editable field.
 */
export const artifactTabRenameOverlayClasses = cn(
  'absolute inset-0 z-30 flex items-center gap-1',
  'rounded-md border border-border bg-surface px-2.5'
);

/** Label text inside a tab trigger. `truncate` (overflow-hidden + ellipsis + nowrap) clamps a
 * long label to the pill's `max-w-48`; `min-w-0` lets this flex child shrink so truncation
 * engages instead of forcing the pill wider.
 * No `leading-none`: `truncate`'s `overflow: hidden` clips against the line box, and a 1.0
 * line-height is shorter than the glyph's full height, so descenders (g, y, p) get trimmed.
 * Inheriting p2's `leading-normal` gives the box enough height for descenders. */
export const artifactTabItemLabelClasses = cn(
  typographyVariant({ variant: 'p2' }),
  'min-w-0 truncate'
);

/** Fixed container for action buttons outside the scrollable list. */
export const artifactTabActionsClasses = 'flex shrink-0 items-center gap-1';
